import { type DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { useAdvancedSearchUX } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import { useDocumentSearchData } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/DocumentSearchDataContext'
import { useSearchActions } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import { useFavoriteDocuments } from 'features/knowledgeDocumentViewer/hooks/useFavoriteDocuments'
import {
  type ChartSearchItem,
  isChartSearchItem,
  isDocumentSearchItem,
  type RecentItem,
} from 'features/knowledgeDocumentViewer/types/searchableItems'
import React, { useCallback, useMemo } from 'react'

import { MAX_LANDING_RECENTS } from './landingScrollConstants'
import { LandingScrollSections } from './LandingScrollSections'

export const LandingRecentsAndFavorites: React.FC = () => {
  const {
    onSelectDocument,
    onOpenNewTab,
    onAddRecentDocument,
    onOpenSensorDashboard,
    onOpenChartDashboard,
    recentItems,
    isFavorited,
    toggleFavorite,
    favoritesDisabled,
    favoritesSaving,
  } = useSearchActions()
  const { recentItems: dataRecentItems } = useDocumentSearchData()
  const { siteId } = useAdvancedSearchUX()
  const { favoriteDocuments, removeFavorite } = useFavoriteDocuments(siteId)

  const allRecents = recentItems.length > 0 ? recentItems : dataRecentItems
  const displayRecentDocuments = useMemo(
    () =>
      allRecents
        .filter((recent) => isDocumentSearchItem(recent.item))
        .slice(0, MAX_LANDING_RECENTS),
    [allRecents]
  )
  const recentCharts = useMemo(
    () =>
      allRecents
        .filter((recent): recent is RecentItem<ChartSearchItem> =>
          isChartSearchItem(recent.item)
        )
        .slice(0, MAX_LANDING_RECENTS),
    [allRecents]
  )

  const handleRecentDocumentSelect = useCallback(
    (doc: DocumentTableRow) => {
      onAddRecentDocument(doc)
      onOpenNewTab({ documentId: doc.id })
    },
    [onAddRecentDocument, onOpenNewTab]
  )

  const handleFavoriteSelect = useCallback(
    (documentId: string) => {
      onSelectDocument({ documentId })
    },
    [onSelectDocument]
  )

  return (
    <LandingScrollSections
      favoriteDocuments={favoriteDocuments}
      displayRecentDocuments={displayRecentDocuments}
      recentCharts={recentCharts}
      onFavoriteSelect={handleFavoriteSelect}
      onRemoveFavorite={removeFavorite}
      onRecentDocumentSelect={handleRecentDocumentSelect}
      onSensorSelect={onOpenSensorDashboard}
      onChartSelect={onOpenChartDashboard}
      favoritesDisabled={favoritesDisabled}
      favoritesSaving={favoritesSaving}
      isFavorited={isFavorited}
      onToggleFavorite={toggleFavorite}
    />
  )
}
